import { ArrowLeft } from "lucide-react";
import React, { ReactNode, useEffect, useRef, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setFilterWidth } from "../store/state/FilterWidth";

const NewFilterContainer = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(true);
  const [fullbtn, setFullBtn] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const handleWidthChange = useMemo(
    () => (width: number) => {
      if (open) {
        const roundedWidth = Math.ceil(width + 30);
        const pixelValue = `${roundedWidth}px`;
        const pageWidth = `calc(100% - ${pixelValue} - 10px)`;
        dispatch(setFilterWidth(pageWidth));
      }
    },
    [dispatch, open]
  );

  // Create a debounced version that we'll use in the observer
  const debouncedHandleWidthChange = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<typeof handleWidthChange>) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        handleWidthChange(...args);
        timeoutId = null;
      }, 100);
    };
  }, [handleWidthChange]);

  // Set up ResizeObserver with debounce
  useEffect(() => {
    if (!containerRef.current) return;
    const currentContainer = containerRef.current;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        debouncedHandleWidthChange(entry.contentRect.width);
      }
    });

    resizeObserver.observe(currentContainer);

    return () => {
      resizeObserver.unobserve(currentContainer);
    };
  }, [debouncedHandleWidthChange]);

  // Handle open/close state changes
  useEffect(() => {
    if (!open) {
      const pageWidth = `calc(100% - 70px)`;
      dispatch(setFilterWidth(pageWidth));
    }
    // When opening, the ResizeObserver will handle the width change
  }, [open, dispatch]);

  return (
    <div className="hide-btn-outer">
      <button
        style={{
          width: fullbtn ? "100px" : "30px",
          overflow: "hidden",
          height: "30px",
          [open ? "right" : "left"]: open ? "-7px" : "20px",
        }}
        onMouseOver={() => setFullBtn(true)}
        onMouseLeave={() => setFullBtn(false)}
        onClick={() => setOpen(!open)}
        className="hide-btn flex items-center justify-between gap-1 transition-all text-nowrap duration-700 ease-in-out"
      >
        <ArrowLeft
          size={18}
          className={`text-primary transition-transform duration-300 ${
            !open ? "rotate-180" : ""
          }`}
        />
        {fullbtn && (
          <>{open ? <span>Hide Filters</span> : <span>Show Filters</span>}</>
        )}
      </button>

      <div
        ref={containerRef}
        className={`transition-all text-wrap  duration-700 ease-in-out bg-white m-[0.8rem] me-1 rounded-2xl`}
        style={{
          width: open ? "180px" : "0px",
          height: "calc(100vh - 95px)",
          transition: "all 0.3s ease-in-out, opacity 0.3s ease-in-out",
          padding: open ? "0px" : "12px",
          paddingRight: open ? "0rem" : "12px",
        }}
      >
        <div
          className="rounded-2xl"
          style={{
            height: "calc(100vh - 95px)",
            overflowY: open ? "auto" : "hidden",
          }}
        >
          <div
            style={{
              opacity: open ? 1 : 0,
            }}
            className="transition-all duration-700 ease-in-out"
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewFilterContainer;
